import { MAX_PRESTAGING_SLOT } from "./constants";
import { clearSlot, writeProjectName, writeSlot } from "./session";

/**
 * Bootstrap hook for the n-projects preview path.
 *
 * <p>On client entry, inspect the URL for a {@code ?slot=N} parameter. The
 * action taken depends on whether this is a fresh staging-preview entry
 * (URL path starts with {@code /staging}) — staging entries are
 * authoritative about what tier this tab is previewing, plain storefront
 * entries are not:
 *
 * <ul>
 *   <li><strong>?slot=N present (any path)</strong>: persist N in
 *       sessionStorage so the fetch + XHR interceptors stamp
 *       {@code X-Project-Slot} on every API call. Invalid N (non-numeric,
 *       out-of-range) clears any prior slot.
 *   <li><strong>?slot= absent on a {@code /staging} entry</strong>: this is
 *       a fresh STAGING-tier preview opened from the Staging Hub. Clear any
 *       stale slot from a previous browsing session. Without this clear,
 *       opening a project preview, exiting the project, then opening a
 *       staging-tier preview leaks the project's slot into the new tab
 *       (Chrome can restore sessionStorage in some open-in-new-tab cases,
 *       and same-tab navigation always preserves it). The reproducer:
 *       click → close → click → close cycle that "eventually works"
 *       — each fresh tab brings down the leaked slot.
 *   <li><strong>?slot= absent on a non-{@code /staging} entry</strong>:
 *       no-op. The admin might be browsing within an existing slot preview
 *       (e.g. navigating to /products/123 from a previously-set
 *       sessionStorage slot). Leave the slot alone.
 * </ul>
 *
 * <p>Two intentional design choices:
 * <ul>
 *   <li><strong>No token validation.</strong> Slot previews are an admin-only
 *       feature, opened from inside the admin UI (which is auth-gated). The
 *       slot DB only contains an in-flight project's edits; the data is no
 *       more sensitive than what's already in staging. A token-equivalent
 *       can be added later by validating a signed slot token via admin-bff.
 *   <li><strong>Slot persists for the browser session.</strong> The admin
 *       navigates through the storefront while inside the preview; the slot
 *       must survive client-side route changes. Closing the tab clears it
 *       (sessionStorage scope).
 * </ul>
 *
 * <p>SSR-safe: no-op when {@code window} or its {@code location} is absent
 * (Gatsby build-time SSR).
 */
export function installSlotBootstrap(): void {
  if (typeof window === "undefined" || !window.location) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("slot");

    if (raw !== null) {
      // Explicit slot value in URL — authoritative.
      const parsed = Number.parseInt(raw, 10);
      // Upper bound is the deployable ceiling; prod runs 10 slots, local/UAT 5.
      if (Number.isNaN(parsed) || parsed < 1 || parsed > MAX_PRESTAGING_SLOT) {
        // Malformed slot → clear any prior session state (slot AND
        // project name are unset together by clearSlot). Keep browsing
        // live as the safe fallback; don't redirect.
        clearSlot();
        return;
      }
      writeSlot(parsed);
      // Project name is optional alongside the slot — admin-bff sends
      // it in &project=<encoded-name> when known, so the preview banner
      // can render 'Pre-Staging — ProjectName' instead of just the
      // slot number. Absent param → leave project-name storage as-is
      // (the slot is the source of truth; banner falls back to
      // 'Project N' if the name isn't persisted).
      const projectName = params.get("project");
      if (projectName !== null && projectName.length > 0) {
        writeProjectName(projectName);
      }
      return;
    }

    // No ?slot= in URL. The behaviour now depends on whether this is a
    // fresh staging-preview entry or a regular storefront navigation.
    if (isStagingEntryPath(window.location.pathname)) {
      // Fresh /staging preview entry without ?slot= → admin opened a
      // staging-tier preview (Staging tab's Preview button, or any
      // shared link). Clear any leftover slot so this tab shows staging
      // data, not whatever the previous session had cached. Fixes the
      // "preview shows previous project, click+close 3 times to see
      // staging" symptom.
      clearSlot();
    }
    // Non-staging entry, no slot in URL → leave sessionStorage alone.
    // The admin might be navigating within an existing slot preview.
  } catch {
    // Any URL parse failure → no-op. Browsing live is the safe fallback.
  }
}

/**
 * Does this URL path indicate a fresh staging-preview entry?
 *
 * <p>The storefront enters preview mode when the path starts with
 * {@code /staging} (e.g. {@code /staging}, {@code /staging/category/phones}).
 * Non-staging paths are regular storefront navigations and should NOT
 * be authoritative about the slot.
 */
function isStagingEntryPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return pathname === "/staging" || pathname.startsWith("/staging/") || pathname.startsWith("/staging?");
}
